// The client's task list — what a caregiver is actually asked to do, and on
// which days. This is the layer between the signed care plan and the visit
// checklist, and it is the one the office may adjust.
//
// The rule: the plan decides WHICH care is required, so adding or removing a
// task means editing the care plan and having the nurse sign it. The office
// decides WHEN and HOW it is delivered — days, frequency, wording — and those
// edits survive the next activation and are shown as differing from the plan.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  DAYS, FREQUENCIES, KIND_LABEL, getClientTasks, setTaskSchedule, resetTaskToPlan,
  scheduleLabel, planScheduleLabel, differsFromPlan, groupByKind,
} from '../../lib/clientTasks';

const field = { width: '100%', padding: '7px 9px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 12.5, fontFamily: 'inherit' };
const label = { fontSize: 10, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 };
const th = { textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500, padding: '6px 8px', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '7px 8px', borderBottom: '0.5px solid var(--border)', fontSize: 12.5, verticalAlign: 'top' };
const sectionHead = { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--accent)', margin: '18px 0 6px', paddingBottom: 4, borderBottom: '1px solid var(--border)' };

const dayChip = (on, dim) => ({
  width: 30, padding: '3px 0', borderRadius: 6, fontSize: 10.5, fontWeight: 500, cursor: dim ? 'default' : 'pointer',
  fontFamily: 'inherit', textAlign: 'center', border: '0.5px solid',
  borderColor: on ? 'var(--accent)' : 'var(--border)',
  background: on ? 'var(--accent)' : 'transparent',
  color: on ? 'var(--white)' : 'var(--text3)',
  opacity: dim ? 0.55 : 1,
});

export default function ClientTaskList({ client, canEdit = true, onChanged }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const say = (m) => { setToast(m); setTimeout(() => setToast(''), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: e } = await getClientTasks(client.id);
    if (e) setError(e.message);
    setTasks(data || []);
    setLoading(false);
  }, [client.id]);
  useEffect(() => { load(); }, [load]);

  const active = useMemo(() => tasks.filter(t => t.active), [tasks]);
  const retired = useMemo(() => tasks.filter(t => !t.active), [tasks]);
  const overridden = active.filter(differsFromPlan).length;

  const openEdit = (t) => setEditing({
    id: t.id,
    name: t.task?.name || '',
    frequency: t.frequency || 'every_visit',
    days: t.days_of_week || [],
    instructions: t.instructions || '',
    note: t.office_note || '',
    planned: planScheduleLabel(t),
    planInstructions: t.plan_instructions || '',
  });

  const save = async () => {
    setBusy(true); setError('');
    try {
      const r = await setTaskSchedule(editing.id, editing);
      setEditing(null);
      await load(); onChanged && onChanged();
      say(`Saved. ${r?.visit_tasks_added || 0} upcoming visit${r?.visit_tasks_added === 1 ? '' : 's'} updated.`);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const reset = async (t) => {
    if (!window.confirm(`Put "${t.task?.name}" back to what the signed care plan asks for?`)) return;
    setBusy(true); setError('');
    try { await resetTaskToPlan(t.id); await load(); onChanged && onChanged(); say('Back to the care plan.'); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (loading) return <p style={{ color: 'var(--text3)', fontSize: 12.5 }}>Loading the task list…</p>;

  return (
    <div>
      {toast && <div style={{ background: 'var(--success-bg)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--success)', marginBottom: 10 }}>✓ {toast}</div>}
      {error && <div style={{ background: 'var(--danger-bg)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--danger)', marginBottom: 10 }}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h4 style={{ margin: 0, fontSize: 14 }}>Task list</h4>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text3)', maxWidth: '62ch' }}>
            What the caregiver sees on each visit. The care plan decides which tasks are here; you decide which days
            they run and what the instruction says. To add or remove a task, edit the care plan above and have the
            nurse sign it.
          </p>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
          <b>{active.length}</b> active{overridden > 0 && <span style={{ color: 'var(--warn)' }}> · {overridden} adjusted</span>}
        </div>
      </div>

      {active.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--text3)', marginTop: 12 }}>
          No tasks yet. They appear once a care plan is signed and activated.
        </p>
      ) : groupByKind(active).map(([kind, kindLabel, rows]) => (
        <div key={kind}>
          <div style={sectionHead}>{kindLabel}</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>Task</th><th style={th}>Days</th><th style={{ ...th, width: 120 }}>How often</th>
                <th style={th}>Instruction</th><th style={{ ...th, width: 92 }} />
              </tr></thead>
              <tbody>
                {rows.map(t => {
                  const changed = differsFromPlan(t);
                  const weekly = t.frequency === 'weekly';
                  return (
                    <tr key={t.id}>
                      <td style={td}>
                        {t.task?.name}
                        {t.required && <span style={{ fontSize: 9.5, marginLeft: 6, padding: '1px 6px', borderRadius: 20, background: 'var(--accent-bg)', color: 'var(--accent2)' }}>required</span>}
                        {t.task?.capture !== 'check' && <span style={{ fontSize: 9.5, marginLeft: 6, padding: '1px 6px', borderRadius: 20, background: 'var(--info-bg)', color: 'var(--info)' }}>records {t.task.capture === 'vitals' ? 'readings' : 'a note'}</span>}
                        <div style={{ fontSize: 10.5, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{t.task?.code}</div>
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: 3 }}>
                          {DAYS.map((d, i) => (
                            <span key={d} style={dayChip(!weekly || (t.days_of_week || []).includes(i), !weekly)}>{d[0]}</span>
                          ))}
                        </div>
                        {!weekly && <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 3 }}>{scheduleLabel(t)}</div>}
                      </td>
                      <td style={td}>{scheduleLabel(t)}</td>
                      <td style={{ ...td, color: 'var(--text2)' }}>
                        {t.instructions || <span style={{ color: 'var(--text3)' }}>—</span>}
                        {changed && (
                          <div style={{ fontSize: 10.5, color: 'var(--warn)', marginTop: 3 }}>
                            Adjusted by the office{t.office_edited_at ? ` on ${format(parseISO(t.office_edited_at), 'd MMM')}` : ''} · plan says {planScheduleLabel(t)}
                            {t.office_note ? ` · ${t.office_note}` : ''}
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {canEdit && <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => openEdit(t)}>Edit</button>}
                        {canEdit && changed && <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, marginLeft: 4 }} disabled={busy} onClick={() => reset(t)}>Reset</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {retired.length > 0 && (
        <details style={{ marginTop: 16 }}>
          <summary style={{ fontSize: 12, color: 'var(--text3)', cursor: 'pointer' }}>
            {retired.length} task{retired.length === 1 ? '' : 's'} no longer on the care plan
          </summary>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6, lineHeight: 1.8 }}>
            {retired.map(t => <div key={t.id}>{t.task?.name} · {KIND_LABEL[t.task?.kind] || t.task?.kind}</div>)}
          </div>
        </details>
      )}

      {editing && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setEditing(null)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header"><span>{editing.name}</span><button className="btn btn-outline btn-sm" onClick={() => setEditing(null)}>Close</button></div>
            <div className="modal-body">
              <div style={{ fontSize: 11.5, color: 'var(--text3)', marginBottom: 14 }}>
                The signed care plan asks for <b>{editing.planned}</b>. Changing it here does not change the plan.
              </div>

              <div style={label}>How often</div>
              <select style={field} value={editing.frequency} onChange={e => setEditing(f => ({ ...f, frequency: e.target.value }))}>
                {FREQUENCIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>

              {editing.frequency === 'weekly' && (
                <div style={{ marginTop: 12 }}>
                  <div style={label}>Days</div>
                  <div style={{ display: 'flex', gap: 5 }}>
                    {DAYS.map((d, i) => (
                      <button key={d} style={{ ...dayChip(editing.days.includes(i)), width: 38, padding: '5px 0' }}
                        onClick={() => setEditing(f => ({ ...f, days: f.days.includes(i) ? f.days.filter(x => x !== i) : [...f.days, i] }))}>
                        {d}
                      </button>
                    ))}
                  </div>
                  {editing.days.length === 0 && <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 5 }}>Pick at least one day, or the task will not appear on any visit.</div>}
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <div style={label}>Instruction the caregiver reads</div>
                <textarea style={{ ...field, minHeight: 64 }} value={editing.instructions}
                  onChange={e => setEditing(f => ({ ...f, instructions: e.target.value }))}
                  placeholder={editing.planInstructions || 'How this task should be done for this client.'} />
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={label}>Why it was changed (for the office)</div>
                <input style={field} value={editing.note} onChange={e => setEditing(f => ({ ...f, note: e.target.value }))}
                  placeholder="Mum bathes her at the weekend." />
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={busy || (editing.frequency === 'weekly' && !editing.days.length)} onClick={save}>
                {busy ? 'Saving…' : 'Save and update visits'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
